// In-app navigation memory used by "Back" controls (MenuSliderBar back
// button, sign-in / sign-up pages). Two sources, both same-tab only:
//   - React Router's history index (BrowserRouter stores { idx } in
//     history.state): > 0 means an Elakay page precedes this entry;
//   - the last few non-auth pages opened in this tab (sessionStorage), so an
//     auth page can offer a precise "back" even after a refresh.
// Only same-origin relative paths are ever stored or returned
// (safePostAuthDestination), never an auth page: no open redirect, no
// sign-in -> sign-in loop.
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { safePostAuthDestination } from "./postAuthRedirect";

export const RECENT_PAGES_KEY = "elakay:recentAppPages";
const MAX_RECENT = 5;
const MAX_AGE_MS = 6 * 60 * 60 * 1000;

export const hasInAppHistory = () => {
  try {
    return Number(window.history.state?.idx) > 0;
  } catch (_) {
    return false;
  }
};

const readRecent = () => {
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(RECENT_PAGES_KEY) || "[]");
    const now = Date.now();
    return (Array.isArray(stored) ? stored : [])
      .filter((entry) => entry && now - Number(entry.at) < MAX_AGE_MS && safePostAuthDestination(entry.path) === entry.path);
  } catch (_) {
    return [];
  }
};

export const recordAppPage = (location) => {
  const path = safePostAuthDestination(`${location?.pathname || ""}${location?.search || ""}${location?.hash || ""}`);
  if (!path) return null;
  const recent = readRecent().filter((entry) => entry.path !== path);
  recent.unshift({ path, at: Date.now() });
  try { window.sessionStorage.setItem(RECENT_PAGES_KEY, JSON.stringify(recent.slice(0, MAX_RECENT))); } catch (_) { /* storage blocked */ }
  return path;
};

// The most recent Elakay page of this tab that is neither the current page
// nor one of `exclude` (e.g. the protected page that sent the visitor to
// /signin: going "back" there would only bounce to /signin again).
export const previousAppPage = ({ current = "", exclude = [] } = {}) => {
  const skip = new Set([current, ...exclude].filter(Boolean).map((path) => String(path).split("#")[0]));
  const found = readRecent().find((entry) => !skip.has(entry.path.split("#")[0]));
  return found ? found.path : null;
};

// Mounted once inside BrowserRouter (Routes.js).
export const NavigationHistoryTracker = () => {
  const location = useLocation();
  useEffect(() => {
    recordAppPage(location);
  }, [location]);
  return null;
};
